import { joinWords } from '@/utils/string';

/**
 * The three snippets the page shows, keyed by the label the copy event reports.
 */
export const SnippetKind = {
  Install: 'JS Snippet',
  Identify: 'JS Identify',
  Track: 'JS Track',
} as const;
export type SnippetKind = (typeof SnippetKind)[keyof typeof SnippetKind];

type IdentifySnippetOptions = {
  /** The workspace identifies people by email alone. */
  isEmailIdentified: boolean;
  /** The workspace identifies people by several identifiers. */
  isMultiIdentifier: boolean;
  /** Email is one of the enabled identifiers. */
  canIdentifyByEmail: boolean;
  /** Every enabled identifier, `cio_id` included, in wire order. */
  allEnabledIdentifiers: readonly string[];
};

/**
 * The tracking-script tag. `siteId` selects the workspace and `assetHost`
 * selects the region's script.
 */
export function installSnippet(siteId: string, assetHost: string): string {
  return `<script type="text/javascript">
  var _cio = _cio || [];
  (function() {
    var a,b,c;a=function(f){return function(){_cio.push([f].
    concat(Array.prototype.slice.call(arguments,0)))}};b=["load","identify",
    "sidentify","track","page","on","off"];for(c=0;c<b.length;c++){_cio[b[c]]=a(b[c])};
    var t = document.createElement('script'),
        s = document.getElementsByTagName('script')[0];
    t.async = true;
    t.id    = 'cio-tracker';
    t.setAttribute('data-site-id', '${siteId}');
    t.setAttribute('data-use-array-params', 'true');

    // Enabled in-app messaging
    t.setAttribute('data-use-in-app', 'true');

    t.src = 'https://assets.customer.io/assets/${assetHost}.js';
    s.parentNode.insertBefore(t, s);
  })();
</script>`;
}

/** How the identify example explains its `id` value, given the workspace's identifiers. */
export function identifierUsageComment(allEnabledIdentifiers: readonly string[]): string {
  const either = allEnabledIdentifiers.length > 1 ? 'either ' : '';
  return `Use ${either}${joinWords([...allEnabledIdentifiers], 'or')}.`;
}

/**
 * The identify example. The `id` value and its comment follow the workspace's
 * identifier configuration, so the example is one a reader can paste.
 */
export function identifySnippet({
  isEmailIdentified,
  isMultiIdentifier,
  canIdentifyByEmail,
  allEnabledIdentifiers,
}: IdentifySnippetOptions): string {
  let idValue = 'prod_287';
  let idComment = 'Unique id for the currently signed in user in your application.';
  let emailLine = "\n    email: 'user@domain.com', // Email of the currently signed in user.";

  if (isEmailIdentified) {
    idValue = 'user@domain.com';
    idComment = 'Use an email address for id since this workspace is email-based';
    emailLine = '';
  } else if (isMultiIdentifier) {
    idValue = canIdentifyByEmail ? 'user@domain.com' : 'prod_287';
    idComment = identifierUsageComment(allEnabledIdentifiers);
    emailLine = '';
  }

  return `<script type="text/javascript">
  _cio.identify({
    // Required attributes
    id: '${idValue}',    // ${idComment}

    // Strongly recommended attributes${emailLine}
    created_at: 1339438758,   // Timestamp in your system that represents when
                              // the user first signed up. You'll want to send it
                              // as seconds since the epoch.

    // Example attributes (you can name attributes what you wish)
    first_name: 'John',       // Add any attributes you'd like to use in the email subject or body.
    last_name: 'Smith',       // First name and last name are shown on people pages.
    plan_name: 'premium'      // To use the example segments, set this to 'free' or 'premium'.
  });
</script>`;
}

/** The track example. It does not vary by workspace. */
export function trackSnippet(): string {
  return `<script type="text/javascript">
  _cio.track("watchedIntroVideo");
  _cio.track("purchased", { price: "23.45" });
</script>`;
}
